import React, { useEffect, useRef, useState } from "react";
import ReactSelect from "react-select";
import moment from "moment";
import "moment/locale/gu";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Filter,
  ListFilter,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchMaterialsListData,
  getMaterials,
  getTotalMaterials,
  getMaterialsFilter,
  setMaterialsFilter,
  getMaterialsLoading,
  setEditMaterial,
  toggleMaterialModal,
} from "../../features/materials/materialsSlice";
import { getSelectedBranch } from "../../features/settings/settingsSlice";
import { useCanAccess } from "../../hooks/useCanAccess";
import useConfirmPopup from "../../context/useConfirmPopup";
import { DELETE, POST, PUT } from "../../services/axios.service";
import { toastStyles, filterSelectStyles } from "../../static/styles";
import { t } from "../../locales";
import { formatSize } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { MaterialsProps, MaterialsFilterType, IFormInput } from "../../types/material";
import MaterialFormModal from "./MaterialFormModal";

const sortByOptions = [
  { value: "id", label: t.materials.sortOptions.id },
  { value: "name", label: t.materials.sortOptions.name },
  { value: "created_at", label: t.materials.sortOptions.created_at },
];

const sortOrderOptions = [
  { value: "asc", label: t.materials.sortOrderOptions.asc },
  { value: "desc", label: t.materials.sortOrderOptions.desc },
];

const MaterialList = () => {
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const confirmDelete = useConfirmPopup();

  moment.locale("gu");

  const materials = useAppSelector(getMaterials);
  const totalMaterials = useAppSelector(getTotalMaterials);
  const filter = useAppSelector(getMaterialsFilter);
  const loading = useAppSelector(getMaterialsLoading);
  const selectedBranch = useAppSelector(getSelectedBranch);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number>(0);

  const { register, control, handleSubmit, reset } =
    useForm<MaterialsFilterType>({
      defaultValues: { ...filter },
    });

  const fetchMaterials = () => {
    dispatch(
      fetchMaterialsListData({
        page,
        size: pageSize,
        search: filter.search,
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder,
      })
    );
  };

  const prevBranchIdRef = useRef<number | null>(null);
  const selectedBranchId = selectedBranch?.id ?? null;

  useEffect(() => {
    if (!selectedBranchId) return;

    const prevBranchId = prevBranchIdRef.current;
    prevBranchIdRef.current = selectedBranchId;

    const isBranchChange =
      prevBranchId !== null && prevBranchId !== selectedBranchId;
    if (isBranchChange && page !== 1) {
      setPage(1);
      return;
    }

    fetchMaterials();
  }, [page, pageSize, filter, selectedBranchId]);

  const onApplyFilters = (data: MaterialsFilterType) => {
    dispatch(
      setMaterialsFilter({
        search: data.search || null,
        sortBy: data.sortBy,
        sortOrder: data.sortOrder,
      })
    );
    setPage(1);
  };

  const handleClearFilters = () => {
    const defaults = {
      search: null as string | null,
      sortBy: "id",
      sortOrder: "desc",
    };
    reset(defaults);
    dispatch(setMaterialsFilter(defaults));
    setPage(1);
  };

  const handleShowMaterialForm = async () => {
    await dispatch(setEditMaterial(null));
    dispatch(toggleMaterialModal(true));
  };

  const handleSaveMaterial = async (data: IFormInput) => {
    try {
      const storeEditMaterial = (await import("../../app/store")).store.getState()
        .materials.editMaterial;

      if (storeEditMaterial && storeEditMaterial.id) {
        const response: any = await PUT(`/v1/materials/${storeEditMaterial.id}`, {
          name: data.name,
          description: data.description,
          sizes: data.sizes,
        });
        if (response.data.status === "success") {
          await dispatch(setEditMaterial(null));
          if (page === 1) {
            fetchMaterials();
          } else {
            setPage(1);
          }
          toast.success(response.data.message, { ...(toastStyles as any) });
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      } else {
        const response: any = await POST("/v1/materials", {
          name: data.name,
          description: data.description,
          sizes: data.sizes,
        });
        if (response.data.status === "success") {
          if (page === 1) {
            fetchMaterials();
          } else {
            setPage(1);
          }
          toast.success(response.data.message, { ...(toastStyles as any) });
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.data?.message ||
          t.common.somethingWentWrong,
        { ...(toastStyles as any) }
      );
    }
  };

  const handleDeleteMaterial = async (id: number) => {
    const confirmed = await confirmDelete(
      t.materials.deleteConfirmText,
      t.materials.deleteConfirmTitle,
      t.materials.deleteConfirmButton
    );

    if (confirmed) {
      setActionLoadingId(id);
      try {
        const response: any = await DELETE(`/v1/materials/${id}`);
        if (response.data.status === "success") {
          toast.success(response.data.message, { ...(toastStyles as any) });
          const newPage =
            materials.length > 1 ? page : Math.max(page - 1, 1);
          if (newPage === page) {
            fetchMaterials();
          } else {
            setPage(newPage);
          }
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message || t.materials.inUseError,
          { ...(toastStyles as any) }
        );
      }
      setActionLoadingId(0);
    }
  };

  const handleEditMaterial = async (material: MaterialsProps) => {
    await dispatch(setEditMaterial(material));
    dispatch(toggleMaterialModal(true));
  };

  const totalPages = Math.ceil(totalMaterials / pageSize);

  const getVisiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const renderSizesSummary = (material: MaterialsProps) => {
    const sizes = material.sizes || [];
    if (sizes.length === 0) return "-";
    const def = sizes.find((s) => s.is_default) || sizes[0];
    const extra = sizes.length - 1;
    return extra > 0 ? `${formatSize(def.size)} (+${extra})` : formatSize(def.size);
  };

  const start = totalMaterials === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalMaterials);

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-3 ml-auto">
          <Button
            variant="outline"
            className="h-10 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {t.materials.filterToggle}
          </Button>

          {canAccess("materials.create") && (
            <Button
              className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              onClick={handleShowMaterialForm}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t.materials.addNewMaterial}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t.materials.filterTitle}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <form onSubmit={handleSubmit(onApplyFilters)}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.materials.searchLabel}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      className="pl-12 h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                      placeholder={t.materials.searchPlaceholder}
                      {...register("search")}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.materials.sortByLabel}
                  </label>
                  <div className="relative">
                    <ListFilter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortBy"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortByOptions}
                          placeholder={t.materials.sortByPlaceholder}
                          value={sortByOptions.find(
                            (o) => o.value === field.value
                          )}
                          onChange={(opt: any) =>
                            field.onChange(opt?.value || "id")
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.materials.sortOrderLabel}
                  </label>
                  <div className="relative">
                    <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortOrder"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortOrderOptions}
                          placeholder={t.materials.sortOrderPlaceholder}
                          value={sortOrderOptions.find(
                            (o) => o.value === field.value
                          )}
                          onChange={(opt: any) =>
                            field.onChange(opt?.value || "desc")
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4 border-t pt-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
                  onClick={handleClearFilters}
                >
                  {t.materials.clearFilters}
                </Button>
                <Button
                  type="submit"
                  className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
                >
                  {t.materials.applyFilters}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && materials.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {t.materials.loading}
              </div>
            </CardContent>
          </Card>
        ) : !loading && materials.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              {t.materials.noRecords}
            </CardContent>
          </Card>
        ) : (
          <div
            className={`space-y-3${
              loading ? " opacity-50 pointer-events-none" : ""
            }`}
          >
            {materials.map((material: MaterialsProps, index: number) => (
              <Card key={material.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="font-medium text-gray-900">
                        {material.name}
                      </span>
                      <div className="text-sm text-gray-500">
                        #{(page - 1) * pageSize + index + 1} · ID: {material.id}
                      </div>
                    </div>
                    <div>
                      {actionLoadingId === material.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67]" />
                      ) : (
                        <div className="flex items-center gap-1">
                          {canAccess("materials.update") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEditMaterial(material)}
                            >
                              <Pencil className="h-4 w-4 text-[#113F67]" />
                            </Button>
                          )}
                          {canAccess("materials.delete") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDeleteMaterial(material.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">
                        {t.materials.sizesLabel}
                      </span>
                      <span className="text-gray-700">
                        {renderSizesSummary(material)}
                      </span>
                    </div>
                    {material.description && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">
                          {t.materials.table.description}
                        </span>
                        <span className="text-gray-700 truncate ml-4">
                          {material.description}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">
                        {t.materials.table.date}
                      </span>
                      <span className="text-gray-700">
                        {moment(material.created_at).format(
                          "DD MMMM, YYYY hh:mm A"
                        )}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">
                    {t.materials.table.srNo}
                  </TableHead>
                  <TableHead>{t.materials.table.name}</TableHead>
                  <TableHead>{t.materials.sizesLabel}</TableHead>
                  <TableHead>{t.materials.table.description}</TableHead>
                  <TableHead>{t.materials.table.date}</TableHead>
                  <TableHead className="text-right">
                    {t.materials.table.actions}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                        {t.materials.loading}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : materials.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 text-gray-500"
                    >
                      {t.materials.noRecords}
                    </TableCell>
                  </TableRow>
                ) : (
                  materials.map((material: MaterialsProps, index: number) => (
                    <TableRow className="align-top" key={material.id}>
                      <TableCell className="font-medium text-gray-500">
                        {(page - 1) * pageSize + index + 1}
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium text-gray-900">
                            {material.name}
                          </span>
                          <div className="text-sm text-gray-500">
                            ID: {material.id}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-600 whitespace-nowrap">
                        {renderSizesSummary(material)}
                      </TableCell>
                      <TableCell className="text-gray-600 max-w-xs truncate">
                        {material.description || "-"}
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {moment(material.created_at).format(
                          "DD MMMM, YYYY hh:mm A"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {actionLoadingId === material.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67] ml-auto" />
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {canAccess("materials.update") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEditMaterial(material)}
                              >
                                <Pencil className="h-4 w-4 text-[#113F67]" />
                              </Button>
                            )}
                            {canAccess("materials.delete") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleDeleteMaterial(material.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && materials.length > 0 && (
        <div className="flex flex-wrap items-center justify-end md:justify-between gap-4">
          <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
            <span>
              {start}-{end} of {totalMaterials} {t.materials.pageOfLabel}
            </span>
            <span>{t.materials.pageSizeLabel}</span>
            <Select
              value={String(pageSize)}
              onValueChange={(val) => {
                setPageSize(Number(val));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getVisiblePages().map((p, i) =>
              typeof p === "string" ? (
                <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                  ...
                </span>
              ) : (
                <Button
                  key={p}
                  variant={page === p ? "default" : "outline"}
                  className={`w-8 h-8 p-0 ${
                    page === p ? "bg-[#113F67] hover:bg-[#0d2f4d]" : ""
                  }`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </Button>
              )
            )}
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Material Form Modal */}
      <MaterialFormModal handleSaveMaterial={handleSaveMaterial} />
    </div>
  );
};

export default MaterialList;
